import { useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { DataGridFlexSales, useDataGridFlexSales, useDataGridSync } from '../DataGridFlexSales';

import {
  masterDataDataSelector,
  reportFilterCountryCodeSelector,
  reportFilterTenderGroupBySelector,
  reportMissingClosingTotalMissingClosingTotalSelector,
  reportSalesTypesSelector,
  reportTenderSelector,
  downloadReportPdf,
} from '../../state';
import { TenderReport } from '../../types';
import handlerFactory, { ReportTenderDataGridContext } from './handlerFactory';

const DataGridTitle = () => {
  const { t } = useTranslation();
  const { loaded, data: totals } = useSelector(
    reportMissingClosingTotalMissingClosingTotalSelector
  );

  let title = t('report-tender.title');

  if (loaded && totals !== null) {
    title += ` (${totals} ${t('sale-totals.labels.missing')})`;
  }
  return <span>{title}</span>;
};

const DataGridConfig = () => {
  const { handler } = useDataGridFlexSales<TenderReport, ReportTenderDataGridContext>();
  const { t } = useTranslation();
  const titles = useMemo(
    () => ({ title: t('report-tender.title'), tableTitle: <DataGridTitle /> }),
    [t]
  );

  const country = useSelector(reportFilterCountryCodeSelector);
  const tenderResource = useSelector(reportTenderSelector);
  const groupBy = useSelector(reportFilterTenderGroupBySelector);
  const masterData = useSelector(masterDataDataSelector);

  const { data: saleTypeOptions } = useSelector(reportSalesTypesSelector);

  const { loading, data } = tenderResource;

  const {
    agreements = [],
    companies = [],
    managements = [],
    regionalManagements = [],
    regions = [],
    supervisions = [],
  } = masterData || {};

  const gridContext = useMemo(
    () => ({
      t,
      country,
      groupBy,
      saleTypeOptions,
      agreements,
      companies,
      managements,
      regionalManagements,
      regions,
      supervisions,
    }),
    [
      t,
      country,
      groupBy,
      saleTypeOptions,
      agreements,
      companies,
      managements,
      regionalManagements,
      regions,
      supervisions,
    ]
  );

  useDataGridSync({
    handler,
    data,
    context: gridContext,
    title: titles,
    loading,
  });

  return null;
};

const DataGridExportConfig = () => {
  const { handler } = useDataGridFlexSales<TenderReport, ReportTenderDataGridContext>();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  useEffect(() => {
    handler.setExportConfig({
      PDF: {
        generator: (table, title) => {
          dispatch(
            downloadReportPdf({
              locale: handler.locale,
              t,
              table,
              title,
              includeMissingClosingTable: false,
              parameters: [
                'countryCode',
                'stores',
                'pos',
                'salesTypes',
                'segments',
                'timeBand',
                'hourRange',
                'startAccountingDate',
                'endAccountingDate',
                'startOperationDate',
                'endOperationDate',
                'startBusinessDate',
                'endBusinessDate',
                'groupBy',
              ],
              opStructures: [
                'regionalMgmt',
                'region',
                'management',
                'supervision',
                'agreement',
                'company',
              ],
            })
          );
        },
        isDispatchAction: true,
      },
    });
  }, [handler, dispatch, t]);

  return null;
};

const ReportTenderDataGrid = () => (
  <DataGridFlexSales handlerFactory={handlerFactory}>
    <DataGridConfig />
    <DataGridExportConfig />
  </DataGridFlexSales>
);

export default ReportTenderDataGrid;
